// @flow

/* eslint-disable react/no-unused-prop-types */

import * as React from 'react';
import { Query } from 'react-apollo';

import type { Actions } from '../../../types';
import Row from '../../../components/Row';
import ActionsColumn from '../../../components/ActionsHeaderColumn';
import getActiveOrderQuery from '../../../graphql/queries/getActiveOrder.query';
import createOrderContentQuery from '../../../graphql/queries/createOrderContent.query';
import Separator from '../../../components/Separator';
import EditOrderAction from '../../../components/actions/EditOrderAction';
import EditOrderNotesAction from '../../../components/actions/EditOrderNotesAction';
import RemoveOrderAction from '../../../components/actions/RemoveOrderAction';
import TransmitOrderAction from '../../../components/actions/TransmitOrderAction';
import MOBOAction from '../../../components/actions/MOBOAction';
import messages from '../../../config/messages';
import {
  updateGridAfterEdit,
  removeTypeNamesFromOrderAccount,
} from '../../../utils';
import AddEditOrderAction from "../../../components/actions/AddEditOrderAction";
import ClearFiltersAction from '../../../components/actions/ClearFiltersAction'
import ItemHistoryAction from "../../../components/actions/ItemHistoryAction";
import { DesktopView, MobileView } from '../../../config/mediaQuery';

type StateControllerProps = {
  dirty?: boolean,
  externalDirty?: string,
  invalidDate: boolean,
  invalidType: boolean,
  children: ({
    dirty: boolean,
    invalidDate: boolean,
    invalidType: boolean,
    saving: boolean,
    transmitting: boolean,
    OrderDate?: string,
    changeProperty: (opt: {
      dirty: boolean,
      saving?: boolean,
      transmitting?: boolean,
      invalidDate?: boolean,
      invalidType?: boolean,
      OrderDate?: string,
    }) => mixed,
  }) => React.Node,
};

type StateControllerState = {
  dirty: boolean,
  invalidDate: boolean,
  invalidType: boolean,
  transmitting: boolean,
  saving: boolean,
  OrderDate?: string,
};

const generateNewState = (nextProps: any) => {
  const keyArray = Object.keys(nextProps);
  const newState = keyArray.reduce((reducer, prop) => {
    reducer[prop] = nextProps[prop];

    return reducer;
  }, {});

  if (Object.keys(newState).length > 0) {
    return newState;
  }

  return null;
};

class StateController extends React.Component<
  StateControllerProps,
  StateControllerState,
> {
  state = {
    dirty: this.props.dirty || false,
    invalidType: this.props.invalidType || false,
    transmitting: false,
    saving: false,
    OrderDate: '',
  };

  oldDerivedStateFromProps(nextProps: StateControllerProps, state) {
    const { externalDirty, ...rest } = nextProps;

    let dirty = {};

    if (externalDirty === 'completed') {
      dirty = {
        dirty: true,
      };
    }

    return this.setState(generateNewState(
      {
        ...rest,
        ...dirty,
      },
      state,
    ));
  }

  componentDidMount() {
    this.oldDerivedStateFromProps(this.props, this.state);
  }

  componentDidUpdate(prevProps: StateControllerProps) {
    if (this.props !== prevProps) {
      this.oldDerivedStateFromProps(this.props, this.state);
    }
  }

  changeProperty = (opt: {
    dirty: boolean,
    saving?: boolean,
    transmitting?: boolean,
    invalidType?: boolean,
    OrderDate?: string,
  }) => {
    const newState = generateNewState(opt, this.state);

    if (newState !== null) {
      // $FlowFixMe
      this.setState(newState);
    }
  };

  render() {
    return this.props.children({
      dirty: this.state.dirty,
      invalidDate: this.props.invalidDate,
      invalidType: this.state.invalidType,
      transmitting: this.state.transmitting,
      saving: this.state.saving,
      changeProperty: this.changeProperty,
      OrderDate: this.state.OrderDate,
    });
  }
}

type Props = {
  minHeight: string,
  onItemUpdated: any => mixed,
  Det: any,
  collection: any,
  onChangeOrderType: any,
  selectedOrderType: string,
  onChangeOrderDate: any,
  selectedOrderDate: string,
  shouldHaveDatePicker?: boolean,
  clearCollectionAndResetChangedOrderItems: () => mixed,
  clearSelectedOrderType: () => mixed,
  refetch: () => mixed,
  moduleRefetch: () => mixed,
  setActiveOrder: () => {},
  setItemHistory: () => {},
  displayRowMenu: Boolean,
};

const ActionsOrganizer = (props: Actions & Props) => {
  const {
    collection,
    ColumnsAction,
    CopyAction,
    ExportAction,
    RefreshAction,
    ResetAction,
    MultiSortAction,
    SaveViewAction,
    getGlobalDisabledState,
    isOneRowSelected,
    loading,
    currentItem,
    moduleName,
    itemAddedStatus,
    onItemUpdated,
    onOrderCreated,
    onOrderRemoved,
    orderCreateStatus,
    onChangeOrderType,
    clearSelectedOrderType,
    selectedOrderType,
    selectedOrderDate,
    onChangeOrderDate,
    shouldHaveDatePicker,
    onItemAdded,
    clearCollectionAndResetChangedOrderItems,
    moduleRefetch,
    clearInternalFilters,
    setActiveOrder,
    setItemHistory,
    displayRowMenu,
    displayMenu
  } = props;

  /**
   * This will be truth for rows that are no longer valid for the selected
   * Order Type, and thus, marked as "remove". We'll use it to disable
   * some controls like the "Edit Item";
   */
  const rowIsNoLongerValid =
    currentItem &&
    currentItem.maxItemQty &&
    currentItem.maxItemQty.ActionPerOrderType === 'remove';

  const rowRanOutOfStock =
    currentItem &&
    currentItem.maxItemQty &&
    currentItem.maxItemQty.ActionPerOrderType === 'outofstock';

  return (
    <Row minHeight={props.minHeight} css={{margin: 'auto'}}>
      <Query query={getActiveOrderQuery}>
        {({ data: dataActiveOrder, loading: loadingActiveOrder }: any) => {
          const activeOrderSource =
            dataActiveOrder && dataActiveOrder.activeOrder
              ? dataActiveOrder.activeOrder
              : {};
          setActiveOrder(
            dataActiveOrder &&
              dataActiveOrder.activeOrder &&
              dataActiveOrder.activeOrder,
          );
          const accountSource =
            activeOrderSource && activeOrderSource.Account
              ? activeOrderSource.Account
              : {};
          const { __typename, ...accountVariables }: any = accountSource;
          const d = collection ? collection.items : props.Det;
          const orderWithItems = Array.isArray(d) && d.length > 0;
          const activeOrderType =
            selectedOrderType || activeOrderSource.OrderTypeValue;
          const disableMobo =
            activeOrderType !== 'S' &&
            activeOrderType !== 'BHS' &&
            activeOrderType !== 'BHI';

          let requireMemberInteraction = false;

          if (collection && Array.isArray(collection.items)) {
            /**
             * If there is at least one row containing the action 'update' it
             * means we need to user to do something before being able to
             * transmit the order.
             */
            requireMemberInteraction = collection.items.some(
              i => i.maxItemQty && i.maxItemQty.ActionPerOrderType === 'update',
            );
          }

          const acctData = {
            AcctNo: accountSource && accountSource.AcctNo,
            AcctName: accountSource && accountSource.AcctName,
            Level: accountSource && accountSource.Level
          }

          return (
            <Query
              query={createOrderContentQuery}
              variables={{
                selectedRow: removeTypeNamesFromOrderAccount(accountVariables),
                date: activeOrderSource.OrderDate || selectedOrderDate,
                selectedOrderType,
              }}
              skip={loadingActiveOrder || dataActiveOrder === null}
              fetchPolicy="network-only"
            >
              {({ data, loading: contentLoading }: any) => (
                <StateController
                  invalidType={
                    contentLoading ? false : !activeOrderSource.OrderTypeValue
                  }
                  invalidDate={
                    shouldHaveDatePicker
                      ? !selectedOrderDate &&
                        (contentLoading
                          ? false
                          : data && data.createOrderContent
                          ? !data.createOrderContent.isValidDate
                          : false)
                      : false
                  }
                >
                  {({
                    dirty,
                    invalidDate,
                    invalidType,
                    saving,
                    transmitting,
                    changeProperty,
                  }) => {
                    // this will be used to refetch the Orders module when an item is added on an Order
                    const onAddItemComplete =
                      activeOrderSource && activeOrderSource.Account
                        ? moduleRefetch
                        : undefined

                    // when an item is no longer valid we'll change the
                    // behavior of the form to only allow the user to
                    // remove the item from the order
                    const showRemoveInsteadOfSave =
                      rowIsNoLongerValid || rowRanOutOfStock



                    const editOrderNotesAction = (
                      <EditOrderNotesAction
                        item={accountVariables}
                        disabled={
                          contentLoading ||
                          loading ||
                          !dataActiveOrder.activeOrder ||
                          transmitting ||
                          saving
                        }
                        onOrderCreated={onOrderCreated}
                        orderCreateStatus={orderCreateStatus}
                        OrderCoop={activeOrderSource.Coop}
                        OrderNote={activeOrderSource.Note}
                        OrderPo={activeOrderSource.Po}
                        OrderCoopOptions={
                          data &&
                          data.createOrderContent &&
                          data.createOrderContent.coopOptions
                        }
                        loading={loading}
                        onComplete={onAddItemComplete}
                        Det={collection ? collection.items : props.Det}
                        ShipTo={activeOrderSource.ShipTo}
                      />
                    );
                    const mobo = (
                      <MOBOAction
                        onItemUpdatedUpdateParentCollection={onItemUpdated}
                        onItemAddedParentCollection={(status) => {
                          if (status === 'completed') {
                            onItemAdded('completed');
                          }
                        }}
                        disabled={
                          getGlobalDisabledState() ||
                          saving ||
                          transmitting ||
                          loading ||
                          disableMobo
                        }
                        item={accountVariables}
                        activeOrder={activeOrderSource}
                        items={collection && collection.items}
                        selectedOrderType={selectedOrderType}
                        moduleRefetch={moduleRefetch}
                      />
                    );
                    const itemHistory = (
                      <ItemHistoryAction
                        disabled={getGlobalDisabledState && !acctData.AcctName}
                        moduleName={moduleName}
                        item={acctData}
                        orderRefetch={moduleRefetch}
                      />
                    );                    
                    const itemHistoryMain = (
                      <ItemHistoryAction
                        disabled={getGlobalDisabledState && !acctData.AcctName}
                        moduleName={moduleName}
                        item={acctData}
                        orderRefetch={moduleRefetch}
                        removeButton
                      />
                    );
                    setItemHistory(itemHistoryMain);
                    const removeOrder = (
                      <RemoveOrderAction
                        onOrderRemoved={onOrderRemoved}
                        disabled={loadingActiveOrder || transmitting || saving}
                      />
                    );
                    const exportAction = (
                      <ExportAction
                        disabled={saving || transmitting || loading}
                      />
                    );
                    const transmitOrderAction = (
                      <TransmitOrderAction
                        disabled={
                          contentLoading ||
                          loading ||
                          (shouldHaveDatePicker &&
                            !selectedOrderDate &&
                            invalidDate) ||
                          invalidType ||
                          saving ||
                          !orderWithItems ||
                          requireMemberInteraction
                        }
                        Det={collection ? collection.items : props.Det}
                        OrderDate={selectedOrderDate}
                        OrderType={selectedOrderType}
                        onMutationCompleted={
                          clearCollectionAndResetChangedOrderItems
                        }
                      />
                    );
                    const addEditOrderAction = (
                      <AddEditOrderAction
                        onItemAdded={onItemAdded}
                        itemAddedStatus={itemAddedStatus || ''}
                        moduleName={moduleName}
                        selectedOrderType={selectedOrderType}
                        selectedOrderDate={selectedOrderDate}
                        disabled={
                          getGlobalDisabledState() ||
                          !isOneRowSelected ||
                          loading ||
                          saving ||
                          transmitting
                        }
                        item={currentItem}
                        onItemUpdated={onItemUpdated}
                        activeOrder={activeOrderSource}
                        buttonText={'Edit Item'}
                        buttonTextLoading={'Editing item...'}
                        buttonTextCompleted={'Item edited!'}
                        buttonIcon={'pencil'}
                        successMsg={
                          showRemoveInsteadOfSave
                            ? messages.addItemToOrder.removeSuccess
                            : messages.addItemToOrder.editSuccess
                        }
                        errorMsg={
                          showRemoveInsteadOfSave
                            ? messages.addItemToOrder.removeFail
                            : messages.addItemToOrder.editFail
                        }
                        title={'Edit Item'}
                        updaters={[updateGridAfterEdit]}
                        onComplete={onAddItemComplete}
                        showRemoveInsteadOfSave={showRemoveInsteadOfSave}
                        moduleRefetch={moduleRefetch}
                      />
                    );
                    const formItems = { displayMenu, editOrderNotesAction, mobo, itemHistory, removeOrder, exportAction, transmitOrderAction };
                    const editOrderAction = (
                      <EditOrderAction
                        OrderDate={activeOrderSource.OrderDate}
                        OrderType={activeOrderSource.OrderTypeValue}
                        OrderAccount={accountVariables}
                        holidays={
                          data &&
                          data.createOrderContent &&
                          data.createOrderContent.disabledDates.holidays
                        }
                        typeOptions={
                          data &&
                          data.createOrderContent &&
                          data.createOrderContent.typeOptions
                        }
                        disabledDays={
                          data &&
                          data.createOrderContent &&
                          data.createOrderContent.disabledDates.days
                        }
                        minSelectableDay={
                          data &&
                          data.createOrderContent &&
                          data.createOrderContent.minDate
                        }
                        maxSelectableDay={
                          data &&
                          data.createOrderContent &&
                          data.createOrderContent.maxDate
                        }
                        isValidType={!invalidType}
                        isValidDate={!invalidDate}
                        disabled={
                          contentLoading ||
                          loading ||
                          !dataActiveOrder.activeOrder
                        }
                        notifyFormValidity={changeProperty}
                        onChangeOrderType={(s, valueFull) =>
                          onChangeOrderType({
                            value: s,
                            savedValue: activeOrderSource.OrderTypeValue,
                            valueFull
                          })
                        }
                        onOrderSaved={clearSelectedOrderType}
                        onChangeOrderDate={onChangeOrderDate}
                        shouldHaveDatePicker={shouldHaveDatePicker}
                        Det={collection ? collection.items : props.Det}
                        refetch={props.refetch}
                        dirty={dirty}
                        onComplete={onAddItemComplete}
                        formItems={formItems}
                      />
                    );

                    return (
                      <>
                        <MobileView >
                          {/* in order to place the items correctly they have to be passed down to the editOrderAction component to build the table */}
                          <div style={{ display: displayMenu ? 'flex' : 'none' }}>{editOrderAction}</div>
                          {displayRowMenu && !displayMenu &&
                            <div className='action-menu-items'>
                              <ActionsColumn>
                                <table>
                                  <tbody>
                                    <tr>
                                      <td><CopyAction /></td>
                                      <td>{addEditOrderAction}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </ActionsColumn>
                            </div>
                          }
                        </MobileView>
                        <DesktopView>
                          <React.Fragment>
                            <ActionsColumn>
                              <RefreshAction
                                disabled={saving || transmitting || loading}
                              />
                              {exportAction}
                            </ActionsColumn>

                            <ActionsColumn>
                              <CopyAction />
                            </ActionsColumn>

                            <Separator />

                            <ActionsColumn>
                              <SaveViewAction
                                disabled={saving || transmitting || loading}
                              />
                              <ResetAction
                                disabled={saving || transmitting || loading}
                              />
                            </ActionsColumn>

                            <ActionsColumn>
                              <ColumnsAction
                                disabled={saving || transmitting || loading}
                              />
                            </ActionsColumn>

                            <Separator />

                            <ActionsColumn>
                              {addEditOrderAction}
                              {mobo}
                            </ActionsColumn>

                            <ActionsColumn>{itemHistory}</ActionsColumn>

                            <Separator />

                            {editOrderAction}
                            <Separator />
                            <ActionsColumn>
                              {editOrderNotesAction}
                              {removeOrder}
                            </ActionsColumn>
                            <Separator />
                            <ActionsColumn>{transmitOrderAction}</ActionsColumn>

                            <Separator />

                            <ActionsColumn>
                              <ClearFiltersAction
                                clearInternalFilters={clearInternalFilters}
                                disabled={loading}
                              />
                            </ActionsColumn>
                            
                            <ActionsColumn>
                              <MultiSortAction />
                            </ActionsColumn>
                            
                          </React.Fragment>
                        </DesktopView>
                      </>
                    );
                  }}
                </StateController>
              )}
            </Query>
          );
        }}
      </Query>
    </Row>
  );
};

export default ActionsOrganizer;
